"use client";

import { OfferDto, SOfferDto, SProduct } from "@/__generated__/graphql";
import { Circle, MoreVert, ShoppingCart, ShoppingCartOutlined } from "@mui/icons-material";
import { Badge, Box, Card, CardActionArea, CardContent, Chip, IconButton, Stack, Typography, useTheme, Link, Button } from "@mui/material";
import Image from "next/image";
import { RevealEffect } from "react-reveal-effect";
import ProductCardColors from "./ProductCardColors";
import ProductCardOfferType from "./ProductCardOfferType";
import { useCartHasProduct } from "@/redux/features/cart/hooks";
import { useBreakPoints } from "@/lib/useBreakPoints";
import NextLink from "next/link";
import ProductCardInBasketLogo from "./ProductCardInBasketLogo";
import ProductImageComponent from "./ProductImageComponent";
import React from "react";
type Props = {
  product: SProduct;
};
function arePropsEqual<P>(prevProps: Readonly<P>, nextProps: Readonly<P>): boolean {
  return JSON.stringify(prevProps) === JSON.stringify(nextProps);
}
function ProductCard({ product }: Props) {
  const { mdAndDown } = useBreakPoints();

  const theme = useTheme();
  const borderRadius = 4;
  return (
    <Card sx={{ borderRadius, height: "100%", background: "transparent" }} elevation={0}>
      <Link
        component={NextLink}
        // disableTouchRipple={true}
        // disableRipple={true}
        underline="none"
        target={mdAndDown ? "_self" : "_blank"}
        href={`/products/${product.code}/${product.title.replaceAll(" ", "-")}`}
        sx={{
          "& .productImage": {
            position: "relative",
            overflow: "hidden", // Ensures the overlay stays within the image bounds
          },
          "& .productImage:after": {
            content: '""', // Necessary for the :after pseudo-element
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0)", // Fully transparent by default
            zIndex: 1,
            transition: "all .1s ease-in-out", // Smooth transition for hover effects
            pointerEvents: "none", // Overlay won't block interactions
          },
          "&:hover .productImage:after": {
            backgroundColor: "rgba(0, 0, 0, 0.03)", // Semi-transparent black on hover
          },
          borderRadius,
          height: "100%",
          display: "flex",
          alignItems: "normal",
          // justifyContent: "start",
          flexDirection: "column",
        }}
      >
        {product.mainImage && (
          <Box  className={"productImage"} sx={{ borderRadius, position: "relative", aspectRatio: 1, width: "100%" }}>
            {/* Vertical dots on top-left */}
            <ProductCardColors colors={product.colors} />
            <ProductCardOfferType offer={product.variant?.offer as SOfferDto} />
            <ProductCardInBasketLogo productId={product.id} />
            <ProductImageComponent src={product.mainImage?.address} alt={product.mainImage?.alt || product.title} />
          </Box>
        )}
        <Box sx={{ py: 1 }}>
          <Typography
            variant="body2"
            component={"h3"}
            whiteSpace={"wrap"}
            sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {product.title}
          </Typography>
        </Box>
        <Stack direction={"row"} alignItems={"end"} justifyContent={"space-between"} mt={"auto"} mx={0.2}>
          {product.variant ? (
            <Stack direction={"row"} alignItems={"end"}>
              {product.variant.offer ? (
                <Stack alignItems={"end"}>
                  <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                    <Typography component={"s"} variant="body2" color={"grey.400"}>
                      {product.variant.price.toLocaleString()}
                      <Typography sx={{ mx: 0.2 }} component={"small"} variant="body2">
                        تومان
                      </Typography>
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "error.main",
                        borderRadius: 4,
                        height: 18,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography sx={{ mx: 1, color: "white.main" }} variant="body2" dir="ltr" lineHeight={0}>
                        {`${product.variant.offer.percentage} %`}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography fontWeight={700} variant="h6" component={"section"}>
                    {product.variant.offer.price.toLocaleString()}{" "}
                    <Typography sx={{ mx: 0.3 }} color={"grey.500"} component={"strong"} variant="h6">
                      تومان
                    </Typography>
                  </Typography>
                </Stack>
              ) : (
                <Typography fontWeight={700} component={"section"} variant="h6">
                  {product.variant.price.toLocaleString()}
                  <Typography sx={{ mx: 0.3 }} color={"grey.500"} component={"strong"} variant="h6">
                    تومان
                  </Typography>
                </Typography>
              )}
            </Stack>
          ) : (
            <Chip
              sx={{ backgroundColor: "grey.50" }}
              label={
                <Typography variant="body2" color={"grey.700"}>
                  ناموجود
                </Typography>
              }
            />
          )}
        </Stack>
      </Link>
    </Card>
  );
}
export default React.memo(ProductCard, arePropsEqual);
